import type { EmployerSnapshot } from '@klukka/client';
import {
  AGREEMENTS,
  fmt,
  fmtDay,
  TAX_YEARS,
  UNION_RATES,
  type AdminLang,
  type CompanySettings,
  type PeriodInfo,
  type PeriodType,
} from '@klukka/core';
import { recordedYears } from '../derive';
import { Fld, Panel, Select, type Tr } from '../widgets';

/**
 * Company configuration.
 *
 * The distinction that matters here: wage rules are the company's to set, tax
 * constants are the state's. Withholding is shown read-only per tax year — it
 * is not a setting anyone gets to disagree with.
 */
export function SettingsTab({ snap, pinfo, tr, lang, onChange }: {
  snap: EmployerSnapshot;
  pinfo: PeriodInfo;
  tr: Tr;
  lang: AdminLang;
  onChange: (patch: Partial<CompanySettings>) => void;
}) {
  const s = snap.company.settings;
  const periods: Array<[PeriodType, string]> = [
    ['month', lang === 'is' ? 'Mánuður' : 'Month'],
    ['half', lang === 'is' ? 'Hálfur mánuður' : 'Half month'],
    ['week', lang === 'is' ? 'Vika' : 'Week'],
  ];
  const years = recordedYears(snap).filter((y) => TAX_YEARS[y]);
  const currentYear = Number(snap.serverToday.slice(0, 4));

  return (
    <div>
      <Panel title={tr('periodCfg')}>
        <div className="seg3" style={{ maxWidth: 380 }}>
          {periods.map(([k, label]) => (
            <button key={k} className={s.periodType === k ? 'on' : ''} onClick={() => onChange({ periodType: k })}>
              {label}
            </button>
          ))}
        </div>
      </Panel>

      <Panel title={tr('wageRules')}>
        <Select
          label={`${tr('agreement')} (${lang === 'is' ? 'sjálfgefið fyrir fyrirtækið' : 'company default'})`}
          width="min(460px, 100%)"
          value={s.agreementId}
          onChange={(id) => {
            const a = AGREEMENTS.find((x) => x.id === id);
            if (a) onChange({ agreementId: a.id, yv1Pct: a.yv1Pct, yv2Pct: a.yv2Pct });
          }}
          options={AGREEMENTS.map((a) => ({ value: a.id, label: a.name }))}
        />
        <div className="s-row" style={{ marginTop: 12 }}>
          <Fld label={lang === 'is' ? 'Yfirvinna 1 (% af mán.launum/klst)' : 'Overtime 1 (% of monthly wage/h)'} value={s.yv1Pct} onChange={(v) => onChange({ yv1Pct: v })} suffix="%" width="230px" />
          <Fld label={lang === 'is' ? 'Yfirvinna 2 (% af mán.launum/klst)' : 'Overtime 2 (% of monthly wage/h)'} value={s.yv2Pct} onChange={(v) => onChange({ yv2Pct: v })} suffix="%" width="230px" />
          <Fld label={lang === 'is' ? 'Orlof (grunnur)' : 'Vacation (base)'} value={s.orlofPct} onChange={(v) => onChange({ orlofPct: v })} suffix="%" width="140px" />
          <Fld label={lang === 'is' ? 'Orlof eftir (ár)' : 'Vacation after (yrs)'} value={s.orlofT2Years} onChange={(v) => onChange({ orlofT2Years: v })} suffix={lang === 'is' ? 'ár' : 'yr'} width="130px" />
          <Fld label="→ %" value={s.orlofT2Pct} onChange={(v) => onChange({ orlofT2Pct: v })} suffix="%" width="100px" />
          <Fld label={lang === 'is' ? 'Orlof eftir (ár)' : 'Vacation after (yrs)'} value={s.orlofT3Years} onChange={(v) => onChange({ orlofT3Years: v })} suffix={lang === 'is' ? 'ár' : 'yr'} width="130px" />
          <Fld label="→ %" value={s.orlofT3Pct} onChange={(v) => onChange({ orlofT3Pct: v })} suffix="%" width="100px" />
        </div>
        <div className="dimtx">{tr('wageNote')}</div>
      </Panel>

      <Panel title={tr('constraints')}>
        <div className="s-row">
          <Fld label={lang === 'is' ? 'Dagvinnumörk (klst/dag)' : 'Daily threshold (h/day)'} value={s.dailyThreshold} onChange={(v) => onChange({ dailyThreshold: v })} suffix="klst" width="200px" />
          <Fld label={lang === 'is' ? 'Vikumörk (klst/viku)' : 'Weekly threshold (h/week)'} value={s.weeklyThreshold} onChange={(v) => onChange({ weeklyThreshold: v })} suffix="klst" width="200px" />
          {/* A list, not a free number: this field saves as it changes, and a
              half-typed "1" on the way to "16" would close every shift. */}
          <Select
            label={lang === 'is' ? 'Hámarkslengd vaktar' : 'Maximum shift length'}
            width="200px"
            value={s.maxShiftHours}
            onChange={(v) => onChange({ maxShiftHours: Number(v) })}
            options={[...new Set([10, 12, 13, 14, 16, 18, 20, 24, s.maxShiftHours])]
              .filter((h) => h >= s.dailyThreshold && h <= 24)
              .sort((a, b) => a - b)
              .map((h) => ({ value: h, label: `${h} klst` }))}
          />
        </div>
        <div className="dimtx">
          {lang === 'is'
            ? 'Dagvinnumörkin ráða líka hvenær appið minnir starfsmann á að stimpla sig út eða staðfesta yfirvinnu.'
            : 'The daily threshold also decides when the app reminds a worker to clock out or confirm overtime.'}
        </div>
        <div className="dimtx">
          {lang === 'is'
            ? 'Vakt sem er enn opin eftir hámarkslengdina telst gleymd útstimplun: henni er lokað án tíma og hún birtist í breytingaskránni, þar sem þú skráir raunverulegan vinnutíma. Lög krefjast 11 klst hvíldar á sólarhring, 8 klst við vaktaskipti.'
            : 'A shift still open after the maximum counts as a forgotten clock-out: it is closed without hours and appears in the edit log, where you enter the hours actually worked. The law requires 11 hours of rest in every 24, or 8 at a shift change.'}
        </div>
      </Panel>

      <Panel title={tr('funds')}>
        <div className="s-row">
          <Fld label={lang === 'is' ? 'Lífeyrir starfsm.' : 'Employee pension'} value={s.pensionEmpPct} onChange={(v) => onChange({ pensionEmpPct: v })} suffix="%" width="150px" />
          <Fld label={lang === 'is' ? 'Mótframlag' : 'Employer match'} value={s.pensionErPct} onChange={(v) => onChange({ pensionErPct: v })} suffix="%" width="150px" />
          <Fld label={lang === 'is' ? 'Mótfr. viðbótar' : 'Match on extra'} value={s.matchExtraPct} onChange={(v) => onChange({ matchExtraPct: v })} suffix="%" width="150px" />
          <Fld label={tr('tryggLbl')} value={s.tryggPct} onChange={(v) => onChange({ tryggPct: v })} suffix="%" width="160px" />
        </div>

        <details className="acc-sec" style={{ marginTop: 12 }}>
          <summary>{tr('unionConstants')}</summary>
          <table className="tbl" style={{ maxWidth: 640, marginBottom: 10 }}>
            <thead>
              <tr><th>{tr('unionCol')}</th><th>{tr('memberFee')}</th><th>{tr('employerFunds')}</th></tr>
            </thead>
            <tbody>
              {Object.entries(UNION_RATES).map(([name, r]) => (
                <tr key={name}>
                  <td><b>{name}</b></td>
                  <td className="mono">{r.member}%</td>
                  <td className="mono">{r.employer}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="dimtx" style={{ fontSize: 12 }}>{tr('unionNote')}</div>
        </details>
      </Panel>

      <Panel title={tr('taxCfg')}>
        {years.map((y) => {
          const c = TAX_YEARS[y];
          return (
            <details className="acc-sec" key={y} open={y === currentYear}>
              <summary>
                {tr('taxYear')} {y}{y === currentYear ? ` · ${tr('taxCurrent')}` : ''}
              </summary>
              <div className="tax-grid">
                <div className="rate"><span>Þrep 1</span><b>{c.r[0]}%</b><i>{tr('upTo')} {fmt(c.t[0])}</i></div>
                <div className="rate"><span>Þrep 2</span><b>{c.r[1]}%</b><i>{tr('upTo')} {fmt(c.t[1])}</i></div>
                <div className="rate"><span>Þrep 3</span><b>{c.r[2]}%</b><i>{tr('above')}</i></div>
                <div className="rate"><span>Persónuafsláttur</span><b>{fmt(c.credit)}</b><i>{tr('perMonth')}</i></div>
                {y === currentYear && s.periodType !== 'month' ? (
                  <div className="rate">
                    <span>{tr('perPeriod')}</span>
                    <b>{fmt(s.periodType === 'half' ? c.credit / 2 : (c.credit * 12 * pinfo.days.length) / 365)}</b>
                    <i>
                      {s.periodType === 'half'
                        ? `${fmt(c.credit * 12)} ÷ 24`
                        : `${fmt(c.credit * 12)} × ${pinfo.days.length} ÷ 365`}
                    </i>
                  </div>
                ) : null}
              </div>
            </details>
          );
        })}
        <div className="dimtx">{tr('taxNote')}</div>
      </Panel>

      <Panel title={`${tr('calendar')} ${currentYear}`}>
        <div className="hol-list">
          {s.holidays.map((h) => (
            <span key={h} className="chip" style={{ borderColor: 'var(--ot2)', color: 'var(--ot2)' }}>{fmtDay(h)}</span>
          ))}
        </div>
        <div className="dimtx">{tr('holidayNote')}</div>
      </Panel>

      <div className="dimtx">{tr('save')}</div>
    </div>
  );
}
